import React, { useMemo } from "react"
import NavDropdown from "../../molecules/NavDropdown"
import NavMegaMenu from "../../molecules/NavMegaMenu"
import ThemeSwitcher from "../../utils/ThemeSwitcher"
import NavLink from "../../atoms/NavLink"
import Box from "../../atoms/Box"
import Flex from "../../atoms/Flex"
import { Title } from "../../atoms/Typography"
import Link from "../../utils/Link"
import Image from "../../utils/Image"
import SocialLink from "../../utils/SocialLink"
import ButtonElement from "../../atoms/ButtonElement"
import Button from "../../atoms/Button"
import HamburgerIcon from "../../icons/Hamburger"

export const normalizeTheme = (themeType, site) => {
  if (themeType === "default") return site?.brand?.theme?.backgroundTheme
  return themeType
}

const getOverrideBool = ({ pageSpecificNavTheme }) => {
  return pageSpecificNavTheme?.overrideSiteStyles
}

const getDefaultTheme = ({
  theme,
  firstSectionStyling,
  pageSpecificNavTheme,
  site,
}) => {
  const overrideNavStyles = getOverrideBool({ pageSpecificNavTheme })
  const isTransparentNav =
    overrideNavStyles && pageSpecificNavTheme?.transparentNav

  let defaultTheme =
    theme === "default"
      ? normalizeTheme(site?.brand?.theme?.backgroundTheme, site)
      : normalizeTheme(theme, site)

  //if page has specified nav theme override default theme
  defaultTheme =
    overrideNavStyles && !isTransparentNav
      ? normalizeTheme(pageSpecificNavTheme?.pageNavTheme, site)
      : overrideNavStyles && isTransparentNav
      ? normalizeTheme(firstSectionStyling?.theme, site)
      : normalizeTheme(theme, site)

  return defaultTheme
}

export const getLogoObj = ({
  theme,
  firstSectionStyling,
  pageSpecificNavTheme,
  site,
  logo,
  logoMedium,
  logoDark,
}) => {
  const defaultTheme = getDefaultTheme({
    theme,
    firstSectionStyling,
    pageSpecificNavTheme,
    site,
  })
  if (defaultTheme === "light" && logo !== null) {
    return logo
  } else if (defaultTheme === "highlight" && logoMedium !== null) {
    return logoMedium
  } else if (defaultTheme === "dark" && logoDark !== null) {
    return logoDark
  } else if (logo !== null) {
    return logo
  } else {
    return null
  }
}

export const getThemeObj = ({
  theme,
  firstSectionStyling,
  pageSpecificNavTheme,
  site,
}) => {
  const defaultTheme = getDefaultTheme({
    theme,
    firstSectionStyling,
    pageSpecificNavTheme,
    site,
  })
  let themeObj = { theme: defaultTheme }
  if (pageSpecificNavTheme?.transparentNav && firstSectionStyling) {
    themeObj = { ...themeObj, bg: firstSectionStyling.bg, shadow: "none" }
  } else if (!pageSpecificNavTheme?.transparentNav && pageSpecificNavTheme) {
    themeObj = { ...themeObj, bg: pageSpecificNavTheme?.bgNav }
  }
  return themeObj
}

export const getHideNavProps = ({
  pageSpecificNavTheme,
  bg,
  navBarDisplay,
  ...rest
}) => {
  const overrideNavStyles = getOverrideBool({ pageSpecificNavTheme })
  let hideNavProps = {
    bg: pageSpecificNavTheme?.transparentNav
      ? "transparent"
      : overrideNavStyles
      ? `background.${pageSpecificNavTheme?.bgNav}`
      : `background.${bg}`,
    position:
      navBarDisplay === "scroll"
        ? "sticky"
        : navBarDisplay === "appear"
        ? "fixed"
        : "relative",
  }
  return hideNavProps
}

export const getLogoShift = ({ logoPosition, layout }) => {
  return layout === "nav-stacked" || layout === "nav-centered"
    ? "center"
    : logoPosition === "left"
    ? "left center"
    : "right center"
}

export const getStyledHeaderProps = ({ pageSpecificNavTheme, bg }) => {
  const overrideNavStyles = getOverrideBool({ pageSpecificNavTheme })
  const isTransparentNav =
    overrideNavStyles && pageSpecificNavTheme?.transparentNav
  return {
    bg: isTransparentNav
      ? "transparent"
      : overrideNavStyles
      ? `background.${pageSpecificNavTheme?.bgNav}`
      : `background.${bg}`,
  }
}

export const getButtonProps = ({ button, button2, logoPosition }) => {
  return {
    ...button,
    ml:
      button && button2
        ? logoPosition === "left"
          ? 6
          : 0
        : logoPosition === "left"
        ? 6
        : 0,
    mr:
      button && button2
        ? logoPosition === "right"
          ? 6
          : 0
        : logoPosition === "right"
        ? 6
        : 0,
  }
}

export const getLinksWrapperProps = ({ logoPosition, navAlignment }) => {
  return {
    flexDirection: logoPosition === "right" ? "row-reverse" : "row",
    justifyContent:
      (logoPosition === "left" && navAlignment === "right") ||
      (logoPosition === "right" && navAlignment === "left")
        ? "flex-end"
        : "space-between",
    pl: logoPosition === "left" && navAlignment === "left" ? 5 : 0,
    pr: logoPosition === "right" && navAlignment === "right" ? 7 : 0,
  }
}

export const renderLogo = (
  {
    logoSize,
    logoPosition,
    siteTitle,
    homepagePageId,
    button,
    button2,
    links,
    layout,
  },
  logoObj,
  logoShift
) => {
  const showHamburger = !!button || !!button2 || !!links?.length
  const isCenteredLayout = layout === "nav-centered" || layout === "nav-stacked"

  const homepageTarget = homepagePageId ?? "/"

  if (!logoObj)
    return (
      <Link
        aria-label="Logo link"
        display="flex"
        flex={logoPosition === "right" ? "0 0 auto" : "1 0 auto"}
        justifyContent={{
          _: !showHamburger && isCenteredLayout ? "center" : "space-between",
          lg: "center",
        }}
        to={homepageTarget}
      >
        <Title variant="h3" as="span">
          {siteTitle}
        </Title>
      </Link>
    )

  return (
    <Box
      flexShrink="0"
      width={{
        _:
          logoSize?.sizeSelections === "custom" &&
          logoSize?.customSizeMobile !== null
            ? `${logoSize?.customSizeMobile}px`
            : 8,
        md:
          logoSize?.sizeSelections === "custom"
            ? `${logoSize?.customSize}px`
            : 13,
      }}
      height={logoSize?.sizeSelections === "custom" ? "100%" : { _: 2, md: 3 }}
    >
      <Link
        aria-label="Logo link"
        to={homepageTarget}
        display={logoSize?.sizeSelections === "custom" ? "contents" : "inline"}
      >
        <Image
          alternativeText="Header Logo"
          {...logoObj}
          style={{ height: "100%" }}
          imgStyle={{
            objectFit: "contain",
            objectPosition: logoShift,
          }}
        />
      </Link>
    </Box>
  )
}

export const renderHeaderLinks = ({
  links,
  button,
  button2,
  dropDownOpenOn,
  dropdownPlacement,
  rightLinks,
  removeFirstPadding,
  bg,
  boxShadow,
  borderRadius,
  theme,
}) => {
  if (!links?.length) return null

  const fallbackCard = { bg, boxShadow, borderRadius, theme } // default card following header theme used when dropdown card are undefined

  return links.map((item, i) => {
    const typeName = item.__typename || item.__component

    switch (typeName) {
      case "STRAPI__COMPONENT_UTILS_LINK":
      case "utils.link":
        return (
          <NavLink
            ml={
              !rightLinks && button && button2
                ? { _: 4, xl: 6 }
                : rightLinks || (removeFirstPadding && i === 0)
                ? 0
                : 6
            }
            mr={rightLinks ? { _: 4, xl: 6 } : 0}
            partiallyActive={true}
            key={i}
            variant="navLink"
            {...item}
          />
        )

      case "STRAPI__COMPONENT_UTILS_DROPDOWN":
      case "utils.dropdown":
        return (
          <NavDropdown
            ml={
              !rightLinks && button && button2
                ? { _: 4, xl: 6 }
                : rightLinks || (removeFirstPadding && i === 0)
                ? 0
                : 6
            }
            mr={rightLinks ? { _: 4, xl: 6 } : 0}
            key={i}
            {...item}
            dropDownOpenOn={dropDownOpenOn}
            dropdownPlacement={dropdownPlacement}
            variant="navLink"
            card={item.card ?? fallbackCard}
          />
        )

      case "STRAPI__COMPONENT_UTILS_MEGA_MENU":
      case "utils.mega-menu":
        return (
          <NavMegaMenu
            ml={
              !rightLinks && button && button2
                ? { _: 4, xl: 6 }
                : rightLinks || (removeFirstPadding && i === 0)
                ? 0
                : 6
            }
            mr={rightLinks ? { _: 4, xl: 6 } : 0}
            key={i}
            {...item}
            dropDownOpenOn={dropDownOpenOn}
            dropdownPlacement={dropdownPlacement}
            variant="navLink"
            card={item.card ?? fallbackCard}
          />
        )

      default:
        return null
    }
  })
}

export const renderSocialLinks = ({
  socialLinks,
  socialLinkStyles,
  socialLinkSize,
  socialLinkSpacing,
}) => {
  const socialStylesObj =
    socialLinkStyles === undefined || socialLinkStyles === null
      ? {
          color: "primary",
          theme: "highlight",
          bg: 3,
          iconStyle: "solid",
        }
      : socialLinkStyles
  const noSocials = !socialLinks || socialLinks.length === 0

  return (
    <>
      {!noSocials && (
        <Flex className="social" mt={3} mb={3}>
          {socialLinks?.map((socialLink, i) => {
            return (
              <Box ml={i === 0 && { _: 4, xl: 6 }} key={`social-${i}`}>
                <SocialLink
                  iconSize={socialLinkSize}
                  {...socialLink}
                  {...socialStylesObj}
                  index={i}
                  socialSpacing={socialLinkSpacing}
                />
              </Box>
            )
          })}
        </Flex>
      )}
    </>
  )
}

export const renderCenteredLayout = (
  props,
  logoObj,
  logoShift,
  themeObj,
  setMenu,
  isMobileBp
) => {
  const showHamburger =
    !!props?.button || !!props?.button2 || !!props?.links?.length

  return (
    <>
      <Box
        height="100%"
        display={{ _: "none", lg: "flex" }}
        justifyContent="center"
      >
        {props?.layout === "nav-centered" &&
          centeredNav(props, logoObj, logoShift)}
        {props?.layout === "nav-stacked" &&
          stackedNav(props, logoObj, logoShift, themeObj)}
      </Box>
      {/* below is mobile menu html */}
      <Box
        display={{ _: "flex", lg: "none" }}
        height="100%"
        justifyContent={{
          _:
            !showHamburger &&
            (props?.layout === "nav-centered" ||
              props?.layout === "nav-stacked")
              ? "center"
              : "space-between",
          lg: "space-between",
        }}
        px={5}
        alignItems="center"
      >
        {renderLogo(
          props,
          logoObj,
          isMobileBp && showHamburger ? "left center" : logoShift
        )}
        <Box display={{ _: "flex", lg: "none" }} alignItems="center">
          {(props?.mobileIconLocation === "navigationBar" ||
            (props?.mobileIconLocation === "belowLinks" && !showHamburger)) &&
            renderSocialLinks(props)}
          {showHamburger && (
            <ButtonElement
              aria-label="Social icon button"
              onClick={() => setMenu(true)}
              ml={5}
              width={2}
              height={2}
              display={{ _: "block", lg: "none" }}
            >
              <HamburgerIcon color="text" />
            </ButtonElement>
          )}
        </Box>
      </Box>
    </>
  )
}

// helper fn that renders a "normal" centered nav bar
const centeredNav = (props, logoObj, logoShift) => {
  const { links, button, button2, buttonAlignment } = props
  const noCtaButtons = !button && !button2
  const linkCount = links?.length
  const Logo = renderLogo(props, logoObj, logoShift)
  let LeftLinks, RightLinks
  if (links) {
    LeftLinks = renderHeaderLinks({
      ...props,
      links: links?.slice(0, Math.ceil(linkCount / 2)),
    })
    RightLinks = renderHeaderLinks({
      ...props,
      links: links?.slice(Math.ceil(linkCount / 2), links.length),
      rightLinks: true,
    })
  }
  return (
    <Flex width="100%" height="100%" alignItems="center">
      <Flex flex="1 1 0" justifyContent="flex-end" alignItems="center">
        {(button || button2) && buttonAlignment === "left" && (
          <Flex>
            {button && (
              <Button
                {...button}
                hideArrow
                aria-label={
                  button?.link?.linkArialLabel?.length > 0
                    ? button?.link?.linkArialLabel
                    : "CTA Button 1"
                }
              />
            )}
            {button2 && (
              <Button
                {...button2}
                ml={props?.logoPosition === "left" ? 6 : 0}
                mr={props?.logoPosition === "right" ? 6 : 0}
                hideArrow
                aria-label={
                  button?.link?.linkArialLabel?.length > 0
                    ? button?.link?.linkArialLabel
                    : "CTA Button 2"
                }
              />
            )}
          </Flex>
        )}
        {(button || button2) &&
          buttonAlignment === "right" &&
          props?.socialLinks &&
          renderSocialLinks(props)}
        {LeftLinks && LeftLinks}
      </Flex>
      <Flex
        justifyContent="space-around"
        alignItems="center"
        height="100%"
        mx={5}
      >
        {Logo}
      </Flex>
      <Flex flex="1 1 0" justifyContent="flex-start" alignItems="center">
        {RightLinks && RightLinks}
        {(button || button2) &&
          buttonAlignment === "left" &&
          props?.socialLinks &&
          renderSocialLinks(props)}
        {noCtaButtons && props?.socialLinks && renderSocialLinks(props)}
        {(button || button2) && buttonAlignment === "right" && (
          <Flex>
            {button && (
              <Button
                {...button}
                hideArrow
                aria-label={
                  button?.link?.linkArialLabel?.length > 0
                    ? button?.link?.linkArialLabel
                    : "CTA Button 1"
                }
              />
            )}
            {button2 && (
              <Button
                {...button2}
                ml={props?.logoPosition === "left" ? 6 : 0}
                mr={props?.logoPosition === "right" ? 6 : 0}
                hideArrow
                aria-label={
                  button?.link?.linkArialLabel?.length > 0
                    ? button?.link?.linkArialLabel
                    : "CTA Button 2"
                }
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

// helper fn that renders "stacked" centered nav bar
const stackedNav = (props, logoObj, logoShift, themeObj) => {
  const {
    button,
    button2,
    navAlignment,
    linkBarTheme,
    linkBarBg,
    isTransparentNav,
    pageSpecificNavTheme,
  } = props
  const logoPosition = "center"
  const linksAlignment =
    navAlignment === "left"
      ? "start"
      : navAlignment === "center"
      ? "center"
      : navAlignment === "right"
      ? "flex-end"
      : "center"

  const Logo = renderLogo(props, logoObj, logoShift)
  const Links = renderHeaderLinks({ ...props, removeFirstPadding: true })
  const makeTransparent =
    pageSpecificNavTheme?.overrideSiteStyles && isTransparentNav

  return (
    <Flex width="100%" flexDirection="column">
      <Flex
        flex="2"
        height="100%"
        alignItems="center"
        justifyContent="center"
        px={5}
        maxHeight="80px"
      >
        <Flex height="100%" flex="1" alignItems="center">
          {logoPosition === "left" && Logo}
          {logoPosition === "right" && (button || button2) && (
            <Flex>
              {button && (
                <Button
                  ml={4}
                  {...button}
                  hideArrow
                  aria-label={
                    button?.link?.linkArialLabel?.length > 0
                      ? button?.link?.linkArialLabel
                      : "CTA Button 1"
                  }
                />
              )}
              {button2 && (
                <Button
                  {...button2}
                  ml={4}
                  mr={props?.logoPosition === "right" ? 6 : 0}
                  hideArrow
                  aria-label={
                    button?.link?.linkArialLabel?.length > 0
                      ? button?.link?.linkArialLabel
                      : "CTA Button 2"
                  }
                />
              )}
            </Flex>
          )}
          {logoPosition === "right" && renderSocialLinks(props)}
        </Flex>
        <Flex
          height="100%"
          flex="1"
          alignItems="center"
          justifyContent="center"
        >
          {logoPosition === "center" && Logo}
        </Flex>
        <Flex
          height="100%"
          flex="1"
          alignItems="center"
          justifyContent="flex-end"
        >
          {logoPosition === "right" && Logo}
          {logoPosition !== "right" && renderSocialLinks(props)}
          {logoPosition !== "right" && (button || button2) && (
            <Flex>
              {button && (
                <Button
                  ml={4}
                  {...button}
                  hideArrow
                  aria-label={
                    button?.link?.linkArialLabel?.length > 0
                      ? button?.link?.linkArialLabel
                      : "CTA Button 1"
                  }
                />
              )}
              {button2 && (
                <Button
                  {...button2}
                  ml={4}
                  mr={props?.logoPosition === "right" ? 6 : 0}
                  hideArrow
                  aria-label={
                    button2?.link?.linkArialLabel?.length > 0
                      ? button2?.link?.linkArialLabel
                      : "CTA Button 2"
                  }
                  //
                />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      <ThemeSwitcher theme={makeTransparent ? themeObj.theme : linkBarTheme}>
        <Flex
          flex="1"
          alignItems="center"
          justifyContent={linksAlignment}
          px={5}
          bg={
            makeTransparent
              ? "transparent"
              : linkBarBg
              ? `background.${linkBarBg}`
              : `background.${props.bg}`
          }
        >
          {Links}
        </Flex>
      </ThemeSwitcher>
    </Flex>
  )
}

export const useMemoizedProps = props => {
  const storage = {
    logoObj: useMemo(() => getLogoObj(props), [
      props.theme,
      props.pageSpecificNavTheme,
      props.pageNavTheme,
    ]),
    themeObj: useMemo(() => getThemeObj(props), [
      props.theme,
      props.pageSpecificNavTheme,
      props.pageNavTheme,
    ]),
    hideNavProps: useMemo(() => getHideNavProps(props), [
      props.pageSpecificNavTheme,
    ]),
    logoShift: useMemo(() => getLogoShift(props), [
      props.logoPosition,
      props.layout,
    ]),
    styledHeaderProps: useMemo(() => getStyledHeaderProps(props), [
      props.pageSpecificNavTheme,
    ]),
    firstButtonProps: useMemo(() => getButtonProps(props), [
      props.button,
      props.button2,
      props.logoPosition,
    ]),
    linksWrapperProps: useMemo(() => getLinksWrapperProps(props), [
      props.logoPosition,
      props.navAlignment,
    ]),
  }

  return Object.keys(storage).reduce((result, field) => {
    result[field] = storage[field]
    return result
  }, {})
}
